/**
 * Управление: машины, люди и доступ к машинам.
 *
 * Экран видит только администратор — но прячется он не поэтому. Настоящая
 * проверка на сервере, здесь лишь то, что нужно человеку, чтобы не ошибиться:
 * понятные последствия и подтверждение там, где ошибка дорогая.
 *
 * Правил здесь нет ни одного. Длина пароля, вид IMEI, запрет удалить машину с
 * поднятым реле, запрет снять права с себя — всё это живёт на сервере и
 * приходит сюда готовым текстом. Вторая копия правила однажды разойдётся
 * с первой, и разойдётся молча.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { BRANDS, BRAND_NAMES } from './brandArt'
import { passportOf, title } from './vehicleId'

import {
  api,
  type Device,
  type Identity,
  type BlockSchedule,
  type JournalRow,
  type Person,
  type Position,
  type Role,
  type Zone,
} from './api'
import { readable } from './apiText'
import { dateTime } from './format'
import { dict, type Dict, type Lang } from './i18n'
import { tankFromLink, tankLiters, tankMissing, typedTank, vin } from './vehicle'

type Section = 'vehicles' | 'people' | 'zones' | 'journal'

type View =
  | { kind: 'list' }
  | { kind: 'newVehicle' }
  | { kind: 'vehicle'; device: Device }
  | { kind: 'newPerson' }
  | { kind: 'person'; person: Person }
  | { kind: 'newZone' }
  | { kind: 'zone'; zone: Zone }

/** Выбор точки на карте: им управляет главный экран, здесь только запрос. */
export type Picking = {
  active: boolean
  point: { latitude: number; longitude: number } | null
  start: () => void
  clear: () => void
}

type Props = {
  identity: Identity
  devices: Device[]
  /** Текущие позиции: VIN живёт в позиции, а не в устройстве. */
  positions: Map<number, Position>
  zones: Zone[]
  lang: Lang
  picking: Picking
  /** Позвать, когда список машин изменился: карта и список должны догнать. */
  onDevicesChanged: () => void
  onZonesChanged: () => void
}

const ROLES: Role[] = ['admin', 'dispatcher', 'manager']

function roleName(role: Role, t: Dict): string {
  if (role === 'admin') return t.mng.roleAdmin
  if (role === 'dispatcher') return t.mng.roleDispatcher
  return t.mng.roleManager
}

function roleHint(role: Role, t: Dict): string {
  if (role === 'admin') return t.mng.roleAdminHint
  if (role === 'dispatcher') return t.mng.roleDispatcherHint
  return t.mng.roleManagerHint
}


export function Manage({
  identity,
  devices,
  positions,
  zones,
  lang,
  picking,
  onDevicesChanged,
  onZonesChanged,
}: Props) {
  const t = dict(lang)
  // Журнал — это /api/journal, а он физически показывает только весь сервер
  // целиком: Traccar не умеет отдать компании её же слайс. Администратору
  // комнаты (у него тоже role === 'admin' — это отдельный, «свой» админ)
  // вкладка не по силам работать, поэтому её не предлагаем вовсе, а не
  // прячем за отказом после клика. Список используется и в полосе вкладок
  // ниже, и в теле — иначе они разойдутся между собой.
  const sections: Section[] = identity.serverAdmin
    ? ['vehicles', 'people', 'zones', 'journal']
    : ['vehicles', 'people', 'zones']
  const [section, setSection] = useState<Section>('vehicles')
  const [view, setView] = useState<View>({ kind: 'list' })
  const [people, setPeople] = useState<Person[] | null>(null)
  const [error, setError] = useState('')

  const loadPeople = useCallback(async () => {
    try {
      setPeople(await api.people())
      setError('')
    } catch (caught) {
      setError(readable(caught, t))
    }
  }, [t])

  useEffect(() => {
    if (section === 'people' && people === null) void loadPeople()
  }, [section, people, loadPeople])

  function backToList() {
    setView({ kind: 'list' })
    setError('')
  }

  // Карточка машины открывается на месте списка, а кнопка, которой её
  // открыли, при этом исчезает — вместе с фокусом: он падает в `body`, и
  // следующее нажатие Tab начинает обход экрана заново, с самого верха.
  // Поэтому фокус переносится на сам сменившийся кусок экрана: дальше Tab
  // идёт по тому, что человек только что открыл. На первой отрисовке ничего
  // не переносим — там фокус ещё ничей, и отнимать его не за что.
  const screenRef = useRef<HTMLDivElement>(null)
  const первая = useRef(true)
  useEffect(() => {
    if (первая.current) {
      первая.current = false
      return
    }
    screenRef.current?.focus()
  }, [view.kind])

  return (
    <div className="manage" ref={screenRef} tabIndex={-1}>
      {view.kind === 'list' && (
        <>
          <div className="chips" role="tablist">
            {sections.map((key) => (
              <button
                key={key}
                role="tab"
                aria-selected={section === key}
                onClick={() => setSection(key)}
              >
                {key === 'vehicles'
                  ? t.mng.vehicles
                  : key === 'people'
                    ? t.mng.people
                    : key === 'zones'
                      ? t.mng.zones
                      : t.mng.journal}
              </button>
            ))}
          </div>

          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}

          {section === 'vehicles' && (
            <>
              {devices.map((device) => (
                <button
                  key={device.id}
                  className="item no-icon"
                  onClick={() => setView({ kind: 'vehicle', device })}
                >
                  <span>
                    <span className="i-name">{title(device)}</span>
                    <span className="i-sub">
                      {device.uniqueId}
                      {tankMissing(positions.get(device.id), tankLiters(device)) &&
                        ` · ${t.mng.tankUnset}`}
                    </span>
                  </span>
                  <span className="chev">›</span>
                </button>
              ))}
              <button className="act" onClick={() => setView({ kind: 'newVehicle' })}>
                {t.mng.addVehicle}
              </button>
            </>
          )}

          {section === 'people' && (
            <>
              {people === null && <p className="hint">{t.loading}</p>}
              {people?.length === 0 && <p className="hint">{t.mng.noPeople}</p>}
              {people?.map((person) => (
                <button
                  key={person.id}
                  className="item no-icon"
                  onClick={() => setView({ kind: 'person', person })}
                >
                  <span>
                    <span className="i-name">
                      {person.name}
                      {person.invite === 'pending' && <em className="tag">{t.mng.invited}</em>}
                      {person.invite === 'unsent' && (
                        <em className="tag">
                          {t.mng.invited} · {t.mng.inviteUnsent}
                        </em>
                      )}
                      {person.invite === 'expired' && (
                        <em className="tag">{t.mng.inviteExpired}</em>
                      )}
                      {!person.invite && person.disabled && (
                        <em className="tag">{t.mng.disabled}</em>
                      )}
                    </span>
                    <span className="i-sub">{roleName(person.role, t)}</span>
                  </span>
                  <span className="chev">›</span>
                </button>
              ))}
              <button className="act" onClick={() => setView({ kind: 'newPerson' })}>
                {t.mng.invitePerson}
              </button>
            </>
          )}

          {section === 'zones' && (
            <>
              {zones.length === 0 && <p className="hint">{t.mng.noZones}</p>}
              {zones.map((zone) => (
                <button
                  key={zone.id}
                  className="item no-icon"
                  onClick={() => setView({ kind: 'zone', zone })}
                >
                  <span>
                    <span className="i-name">{zone.name}</span>
                    <span className="i-sub">
                      {zone.circle
                        ? `${Math.round(zone.circle.radius)} ${t.unitMeter}`
                        : t.mng.notACircle}
                    </span>
                  </span>
                  <span className="chev">›</span>
                </button>
              ))}
              <button
                className="act"
                onClick={() => {
                  picking.clear()
                  setView({ kind: 'newZone' })
                }}
              >
                {t.mng.addZone}
              </button>
            </>
          )}

          {section === 'journal' && sections.includes('journal') && (
            <Journal t={t} lang={lang} devices={devices} />
          )}
        </>
      )}

      {view.kind === 'newVehicle' && (
        <VehicleForm
          t={t}
          onDone={() => {
            onDevicesChanged()
            backToList()
          }}
          onCancel={backToList}
        />
      )}

      {view.kind === 'vehicle' && (
        <VehicleCard
          device={view.device}
          position={positions.get(view.device.id)}
          zones={zones}
          t={t}
          onDone={() => {
            onDevicesChanged()
            backToList()
          }}
          onCancel={backToList}
        />
      )}

      {view.kind === 'newZone' && (
        <ZoneForm
          t={t}
          picking={picking}
          onDone={() => {
            picking.clear()
            onZonesChanged()
            backToList()
          }}
          onCancel={() => {
            picking.clear()
            backToList()
          }}
        />
      )}

      {view.kind === 'zone' && (
        <ZoneCard
          zone={view.zone}
          t={t}
          onDone={() => {
            onZonesChanged()
            backToList()
          }}
          onCancel={backToList}
        />
      )}

      {view.kind === 'newPerson' && (
        <PersonForm
          t={t}
          onDone={() => {
            setPeople(null)
            backToList()
          }}
          onCancel={backToList}
        />
      )}

      {view.kind === 'person' && (
        <PersonCard
          person={view.person}
          devices={devices}
          identity={identity}
          t={t}
          onDone={() => {
            setPeople(null)
            backToList()
          }}
          onCancel={backToList}
        />
      )}
    </div>
  )
}

// --- машины -------------------------------------------------------------------

function VehicleForm({
  t,
  onDone,
  onCancel,
}: {
  t: Dict
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [imei, setImei] = useState('')
  const [plate, setPlate] = useState('')
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')
  const [tank, setTank] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.addDevice({
        name,
        imei,
        plate,
        brand,
        vehicleModel: model,
        tankLiters: typedTank(tank),
      })
      onDone()
    } catch (caught) {
      setError(readable(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <button type="button" className="back" onClick={onCancel}>
        ‹ {t.mng.vehicles}
      </button>
      <h3 className="c-name">{t.mng.addVehicle}</h3>

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      <label className="field">
        <span>{t.mng.name}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>
      <label className="field">
        <span>{t.mng.imei}</span>
        <input
          value={imei}
          onChange={(e) => setImei(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
        />
      </label>

      {/* Госномер обязателен: по нему машину зовут в парке и по нему же её
          сводит со своей учётная система. Марка и модель — по-прежнему нет:
          трекер ставят и до того, как под рукой оказались документы. */}
      <label className="field">
        <span>{t.mng.plate}</span>
        <input
          value={plate}
          onChange={(e) => setPlate(e.target.value)}
          required
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
        />
      </label>
      <label className="field">
        <span>{t.mng.brand}</span>
        <select value={brand} onChange={(e) => setBrand(e.target.value)}>
          <option value="">{t.mng.brandUnset}</option>
          {BRANDS.map((id) => (
            <option key={id} value={id}>
              {BRAND_NAMES[id]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t.mng.model}</span>
        <input
          value={model}
          onChange={(e) => setModel(e.target.value)}
          autoComplete="off"
          placeholder={t.mng.modelHint}
        />
      </label>
      <TankField value={tank} onChange={setTank} t={t} />

      <button className="act" type="submit" disabled={busy}>
        {busy ? t.working : t.mng.save}
      </button>
    </form>
  )
}

/**
 * Объём бака. Границы проверяет сервер — здесь только ввод.
 *
 * `missing` — машина шлёт проценты, а литров без объёма бака не посчитать:
 * тогда вместо пояснения стоит просьба заполнить поле. `locked` — объём
 * прислан из KEPwise: поле только показывает число и говорит, где его менять.
 */
function TankField({
  value,
  onChange,
  missing = false,
  locked = false,
  t,
}: {
  value: string
  onChange: (value: string) => void
  missing?: boolean
  locked?: boolean
  t: Dict
}) {
  const note = locked ? t.mng.tankFromLink : missing ? t.mng.tankAsk : t.mng.tankHint
  return (
    <>
      <label className="field">
        <span>{t.mng.tank}</span>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="decimal"
          autoComplete="off"
          readOnly={locked}
        />
      </label>
      <p className="tank-note">{note}</p>
    </>
  )
}

function VehicleCard({
  device,
  position,
  zones,
  t,
  onDone,
  onCancel,
}: {
  device: Device
  position: Position | undefined
  zones: Zone[]
  t: Dict
  onDone: () => void
  onCancel: () => void
}) {
  const was = passportOf(device)
  const [name, setName] = useState(device.name)
  const [plate, setPlate] = useState(was.plate ?? '')
  const [brand, setBrand] = useState<string>(was.brand ?? '')
  const [model, setModel] = useState(was.model ?? '')
  const wasTank = tankLiters(device)
  const [tank, setTank] = useState(wasTank === null ? '' : String(wasTank))
  // Объём из KEPwise здесь не правят и обратно не шлют: отправка сняла бы с
  // него метку учёта, и число стало бы «вписанным у нас».
  const tankLocked = tankFromLink(device)
  // Кнопка «Сохранить» оживает, только если что-то и правда поменяли: иначе
  // человек жмёт её на всякий случай и не понимает, сработало ли.
  const changed =
    name.trim() !== device.name ||
    plate.trim() !== (was.plate ?? '') ||
    brand !== (was.brand ?? '') ||
    model.trim() !== (was.model ?? '') ||
    // Стёртое поле — не правка: объём бака можно поменять, но не убрать.
    (!tankLocked && typedTank(tank) !== undefined && typedTank(tank) !== wasTank)
  const [watched, setWatched] = useState<Set<number> | null>(null)
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Связка «зона — машина» спрашивается только отсюда: обратного фильтра у
  // Traccar нет, он на /api/devices?geofenceId молча отдаёт весь парк.
  // Один запрос независимо от того, сколько машин в парке.
  useEffect(() => {
    let cancelled = false
    api
      .zones(device.id)
      .then((list) => {
        if (!cancelled) setWatched(new Set(list.map((z) => z.id)))
      })
      .catch((caught) => {
        if (!cancelled) setError(readable(caught, t))
      })
    return () => {
      cancelled = true
    }
  }, [device.id, t])

  async function run(action: () => Promise<unknown>, then?: () => void) {
    setBusy(true)
    setError('')
    try {
      await action()
      then ? then() : onDone()
    } catch (caught) {
      // Сюда приходит и отказ удалить машину с поднятым реле — с объяснением,
      // что делать. Показываем его целиком и остаёмся на экране.
      setError(readable(caught, t))
      setAsking(false)
    } finally {
      setBusy(false)
    }
  }

  async function toggleZone(zoneId: number) {
    if (!watched) return
    const had = watched.has(zoneId)
    await run(
      () => (had ? api.unwatchZone(zoneId, device.id) : api.watchZone(zoneId, device.id)),
      () => {
        const next = new Set(watched)
        had ? next.delete(zoneId) : next.add(zoneId)
        setWatched(next)
      },
    )
  }

  return (
    <div>
      <button className="back" onClick={onCancel}>
        ‹ {t.mng.vehicles}
      </button>
      <h3 className="c-name">{title(device)}</h3>

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      <label className="field">
        <span>{t.mng.name}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">
        <span>{t.mng.plate}</span>
        <input
          value={plate}
          onChange={(e) => setPlate(e.target.value)}
          required
          autoCapitalize="characters"
          spellCheck={false}
        />
      </label>
      <label className="field">
        <span>{t.mng.brand}</span>
        <select value={brand} onChange={(e) => setBrand(e.target.value)}>
          <option value="">{t.mng.brandUnset}</option>
          {BRANDS.map((id) => (
            <option key={id} value={id}>
              {BRAND_NAMES[id]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t.mng.model}</span>
        <input value={model} onChange={(e) => setModel(e.target.value)} />
      </label>
      <TankField
        value={tank}
        onChange={setTank}
        missing={tankMissing(position, wasTank)}
        locked={tankLocked}
        t={t}
      />

      <div className="kv">
        <span>{t.mng.imei}</span>
        <b>{device.uniqueId}</b>
      </div>

      <VinRow position={position} t={t} />

      <button
        className="act"
        disabled={busy || !changed}
        onClick={() =>
          void run(() =>
            api.renameDevice(device.id, {
              name,
              plate,
              brand,
              vehicleModel: model,
              tankLiters: tankLocked ? undefined : typedTank(tank),
            }),
          )
        }
      >
        {busy ? t.working : t.mng.save}
      </button>

      <SwapTrackerBox device={device} t={t} onDone={onDone} />

      <ScheduleBox deviceId={device.id} t={t} />

      {zones.length > 0 && (
        <>
          <h4 className="c-sub">{t.mng.zoneWatchers}</h4>
          {watched === null ? (
            <p className="hint">{t.loading}</p>
          ) : (
            <div className="rows">
              {zones.map((zone) => (
                <label className="kv toggle" key={zone.id}>
                  <span>{zone.name}</span>
                  <input
                    type="checkbox"
                    checked={watched.has(zone.id)}
                    disabled={busy}
                    onChange={() => void toggleZone(zone.id)}
                  />
                </label>
              ))}
            </div>
          )}
        </>
      )}

      <button className="act ghost" onClick={() => setAsking(true)} disabled={busy}>
        {t.mng.remove}
      </button>

      {asking && (
        <div className="confirm" role="dialog" aria-modal="true">
          <div className="cbox">
            <h4>{t.mng.removeVehicle}</h4>
            <p>
              <b>{title(device)}</b> — {t.mng.removeVehicleText}
            </p>
            <button
              className="act danger"
              disabled={busy}
              onClick={() => void run(() => api.removeDevice(device.id))}
            >
              {busy ? t.working : t.mng.remove}
            </button>
            <button className="act" onClick={() => setAsking(false)} disabled={busy}>
              {t.confirmNo}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// --- люди ---------------------------------------------------------------------

function RolePicker({
  value,
  onChange,
  t,
}: {
  value: Role
  onChange: (role: Role) => void
  t: Dict
}) {
  return (
    <div className="roles" role="radiogroup" aria-label={t.mng.role}>
      {ROLES.map((role) => (
        <button
          key={role}
          type="button"
          role="radio"
          aria-checked={value === role}
          onClick={() => onChange(role)}
        >
          <b>{roleName(role, t)}</b>
          <span>{roleHint(role, t)}</span>
        </button>
      ))}
    </div>
  )
}

function PersonForm({
  t,
  onDone,
  onCancel,
}: {
  t: Dict
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  // Умолчание — наименьшие права. Забытая роль не должна раздавать доступ.
  const [role, setRole] = useState<Role>('manager')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Пароля в форме нет намеренно: его назначит себе сам человек по ссылке
  // из письма. Задать пароль новому человеку отсюда нельзя вовсе.
  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.invitePerson(name, email, role)
      onDone()
    } catch (caught) {
      setError(readable(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <button type="button" className="back" onClick={onCancel}>
        ‹ {t.mng.people}
      </button>
      <h3 className="c-name">{t.mng.invitePerson}</h3>
      <p className="hint">{t.mng.inviteHint}</p>

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      <label className="field">
        <span>{t.mng.fullName}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
      </label>
      <label className="field">
        <span>{t.mng.inviteEmail}</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="off"
          autoCapitalize="none"
          inputMode="email"
          required
        />
      </label>

      <RolePicker value={role} onChange={setRole} t={t} />

      <button className="act" type="submit" disabled={busy}>
        {busy ? t.working : t.mng.inviteSend}
      </button>
    </form>
  )
}


function PersonCard({
  person,
  devices,
  identity,
  t,
  onDone,
  onCancel,
}: {
  person: Person
  devices: Device[]
  identity: Identity
  t: Dict
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState(person.name)
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<Role>(person.role)
  const [granted, setGranted] = useState<Set<number> | null>(null)
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sentOk, setSentOk] = useState(false)

  // Себя нельзя понизить и отключить — сервер откажет. Кнопки лучше не
  // показывать вовсе, чем показывать заведомо отказные.
  const itself = identity.id === person.id

  useEffect(() => {
    let cancelled = false
    api
      .personDevices(person.id)
      .then((list) => {
        if (!cancelled) setGranted(new Set(list.map((d) => d.id)))
      })
      .catch((caught) => {
        if (!cancelled) setError(readable(caught, t))
      })
    return () => {
      cancelled = true
    }
  }, [person.id, t])

  async function run(action: () => Promise<unknown>, then?: () => void) {
    setBusy(true)
    setError('')
    try {
      await action()
      then ? then() : onDone()
    } catch (caught) {
      setError(readable(caught, t))
      setAsking(false)
    } finally {
      setBusy(false)
    }
  }

  async function toggleAccess(deviceId: number) {
    if (!granted) return
    const had = granted.has(deviceId)
    await run(
      () => (had ? api.revoke(person.id, deviceId) : api.grant(person.id, deviceId)),
      () => {
        const next = new Set(granted)
        had ? next.delete(deviceId) : next.add(deviceId)
        setGranted(next)
      },
    )
  }

  const changed = name.trim() !== person.name || password !== '' || role !== person.role

  return (
    <div>
      <button className="back" onClick={onCancel}>
        ‹ {t.mng.people}
      </button>
      <h3 className="c-name">{person.name}</h3>
      <p className="c-state">
        <em>{person.email}</em>
      </p>

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      <label className="field">
        <span>{t.mng.fullName}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      {/* У приглашённого пароля ещё нет, и задать его за него нельзя: его
          назначит сам человек по ссылке. Поле появляется после первого входа. */}
      {!person.invite && (
        <label className="field">
          <span>{t.mng.newPassword}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="—"
          />
        </label>
      )}

      {!itself && <RolePicker value={role} onChange={setRole} t={t} />}

      <button
        className="act"
        disabled={busy || !changed}
        onClick={() =>
          void run(() =>
            api.editPerson(person.id, {
              name,
              ...(password ? { password } : {}),
              ...(itself ? {} : { role }),
            }),
          )
        }
      >
        {busy ? t.working : t.mng.save}
      </button>

      <h4 className="c-sub">{t.mng.access}</h4>
      {granted === null ? (
        <p className="hint">{t.loading}</p>
      ) : devices.length === 0 ? (
        <p className="hint">{t.mng.noAccess}</p>
      ) : (
        <div className="rows">
          {devices.map((device) => (
            <label className="kv toggle" key={device.id}>
              <span>{title(device)}</span>
              <input
                type="checkbox"
                checked={granted.has(device.id)}
                disabled={busy}
                onChange={() => void toggleAccess(device.id)}
              />
            </label>
          ))}
        </div>
      )}

      {person.invite && (
        <>
          <h4 className="c-sub">{t.mng.invited}</h4>
          <p className="hint">
            {person.invite === 'unsent' && t.mng.inviteUnsent}
            {person.invite === 'expired' && t.mng.inviteExpired}
            {person.invite === 'pending' && t.mng.inviteHint}
          </p>
          {sentOk && (
            <p className="hint" role="status">
              {t.mng.inviteSentOk}
            </p>
          )}
          <button
            className="act ghost"
            disabled={busy}
            onClick={() =>
              void run(
                () => api.resendInvite(person.id),
                () => setSentOk(true),
              )
            }
          >
            {t.mng.inviteResend}
          </button>
          <button
            className="act ghost"
            disabled={busy}
            onClick={() => void run(() => api.revokeInvite(person.id))}
          >
            {t.mng.inviteRevoke}
          </button>
        </>
      )}

      {/* Подписка на события. Заведённые в приложении получают её сразу; эта
          кнопка — для тех, кто был создан до появления такой возможности, и
          для случая, когда подписка почему-то не оформилась. */}
      <button
        className="act ghost"
        disabled={busy}
        onClick={() => void run(() => api.subscribe(person.id), () => undefined)}
      >
        {t.subscribe}
      </button>

      {!itself && (
        <>
          {/* Приглашённого включит принятие приглашения, а не эта кнопка. */}
          {!person.invite && (
            <button
              className="act ghost"
              disabled={busy}
              onClick={() =>
                void run(() => api.editPerson(person.id, { disabled: !person.disabled }))
              }
            >
              {person.disabled ? t.mng.enable : t.mng.disable}
            </button>
          )}

          <button className="act ghost" onClick={() => setAsking(true)} disabled={busy}>
            {t.mng.remove}
          </button>
        </>
      )}

      {asking && (
        <div className="confirm" role="dialog" aria-modal="true">
          <div className="cbox">
            <h4>{t.mng.removePerson}</h4>
            <p>
              <b>{person.name}</b> — {t.mng.removePersonText}
            </p>
            <button
              className="act danger"
              disabled={busy}
              onClick={() => void run(() => api.removePerson(person.id))}
            >
              {busy ? t.working : t.mng.remove}
            </button>
            <button className="act" onClick={() => setAsking(false)} disabled={busy}>
              {t.confirmNo}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// --- зоны ---------------------------------------------------------------------

/** Радиус по умолчанию: двор или площадка, самый частый случай. */
const DEFAULT_RADIUS = 300

function ZoneForm({
  t,
  picking,
  onDone,
  onCancel,
}: {
  t: Dict
  picking: Picking
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [radius, setRadius] = useState(String(DEFAULT_RADIUS))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const point = picking.point

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!point) {
      picking.start()
      return
    }
    setBusy(true)
    setError('')
    try {
      await api.addZone(name, point.latitude, point.longitude, Number(radius))
      onDone()
    } catch (caught) {
      setError(readable(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <button type="button" className="back" onClick={onCancel}>
        ‹ {t.mng.zones}
      </button>
      <h3 className="c-name">{t.mng.addZone}</h3>

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      <label className="field">
        <span>{t.mng.name}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>
      <label className="field">
        <span>{t.mng.radius}</span>
        <input
          value={radius}
          onChange={(e) => setRadius(e.target.value)}
          inputMode="numeric"
        />
      </label>

      {/* Точка выбирается нажатием на карту, а не вводом координат: широту с
          долготой в поля никто вручную не переписывает. */}
      <button type="button" className="act ghost" onClick={picking.start}>
        {point ? t.mng.pickAgain : t.mng.pickOnMap}
      </button>

      {point && (
        <div className="kv">
          <span>{t.csv.latitude}</span>
          <b>{point.latitude.toFixed(5)}</b>
        </div>
      )}
      {point && (
        <div className="kv">
          <span>{t.csv.longitude}</span>
          <b>{point.longitude.toFixed(5)}</b>
        </div>
      )}

      <button className="act" type="submit" disabled={busy || !point}>
        {busy ? t.working : t.mng.save}
      </button>
    </form>
  )
}

function ZoneCard({
  zone,
  t,
  onDone,
  onCancel,
}: {
  zone: Zone
  t: Dict
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState(zone.name)
  const [radius, setRadius] = useState(zone.circle ? String(Math.round(zone.circle.radius)) : '')
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function run(action: () => Promise<unknown>) {
    setBusy(true)
    setError('')
    try {
      await action()
      onDone()
    } catch (caught) {
      setError(readable(caught, t))
      setAsking(false)
    } finally {
      setBusy(false)
    }
  }

  const changed =
    name.trim() !== zone.name ||
    (zone.circle !== null && Number(radius) !== Math.round(zone.circle.radius))

  return (
    <div>
      <button className="back" onClick={onCancel}>
        ‹ {t.mng.zones}
      </button>
      <h3 className="c-name">{zone.name}</h3>

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      <label className="field">
        <span>{t.mng.name}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>

      {zone.circle ? (
        <label className="field">
          <span>{t.mng.radius}</span>
          <input value={radius} onChange={(e) => setRadius(e.target.value)} inputMode="numeric" />
        </label>
      ) : (
        <p className="hint">{t.mng.notACircle}</p>
      )}

      <button
        className="act"
        disabled={busy || !changed}
        onClick={() =>
          void run(() =>
            api.editZone(zone.id, {
              name,
              ...(zone.circle ? { radius: Number(radius) } : {}),
            }),
          )
        }
      >
        {busy ? t.working : t.mng.save}
      </button>

      <button className="act ghost" onClick={() => setAsking(true)} disabled={busy}>
        {t.mng.remove}
      </button>

      {asking && (
        <div className="confirm" role="dialog" aria-modal="true">
          <div className="cbox">
            <h4>{t.mng.removeZone}</h4>
            <p>
              <b>{zone.name}</b> — {t.mng.removeZoneText}
            </p>
            <button
              className="act danger"
              disabled={busy}
              onClick={() => void run(() => api.removeZone(zone.id))}
            >
              {busy ? t.working : t.mng.remove}
            </button>
            <button className="act" onClick={() => setAsking(false)} disabled={busy}>
              {t.confirmNo}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// --- журнал блокировок ---------------------------------------------------------

/** Сколько дней истории показывать. Тридцать — обычный расчётный период. */
const JOURNAL_DAYS = 30

function Journal({ t, lang, devices }: { t: Dict; lang: Lang; devices: Device[] }) {
  // Журнал приходит с сервера с именем из Traccar. Показываем госномер: в
  // списке машин человек видит именно его, и запись «кто кого заблокировал»
  // обязана звать машину так же, иначе её не сопоставить с парком.
  const plateById = new Map(devices.map((d) => [d.id, title(d)]))
  const [rows, setRows] = useState<JournalRow[] | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    const until = new Date()
    const since = new Date(until.getTime() - JOURNAL_DAYS * 24 * 3600_000)
    api
      .journal(since, until)
      .then((data) => {
        if (!cancelled) setRows(data)
      })
      .catch((caught) => {
        if (!cancelled) setError(readable(caught, t))
      })
    return () => {
      cancelled = true
    }
  }, [t])

  if (error) {
    return (
      <div className="login-error" role="alert">
        {error}
      </div>
    )
  }
  if (rows === null) return <p className="hint">{t.loading}</p>
  if (rows.length === 0) return <p className="hint">{t.mng.journalEmpty}</p>

  return (
    <div className="rows">
      {rows.map((row, i) => (
        <div className="rec" key={`${row.at}-${i}`}>
          <div className="rec-top">
            <b>{plateById.get(row.deviceId) ?? row.deviceName}</b>
            <span>{dateTime(row.at, lang, true)}</span>
          </div>
          {/* Неподтверждённая команда помечена отдельно и намеренно: «команда
              ушла» и «машина заблокирована» — разные вещи, и путать их
              опаснее всего именно здесь. */}
          <div className="rec-sub" data-st={row.confirmed ? undefined : 'warn'}>
            {row.who ?? '—'}{' '}
            {row.action === 'block'
              ? t.mng.didBlock
              : row.action === 'unblock'
                ? t.mng.didUnblock
                : t.mng.notConfirmed}
          </div>
        </div>
      ))}
    </div>
  )
}

// --- расписание блокировки -----------------------------------------------------

const DEFAULT_SCHEDULE: BlockSchedule = {
  enabled: true,
  tz: 'Europe/Berlin',
  workFrom: '06:00',
  workUntil: '20:00',
  workDays: [1, 2, 3, 4, 5],
}

/**
 * Расписание запирает машину само, без человека, поэтому здесь всё названо
 * своими словами: не «правило», а смена, и не «активно», а рабочие дни.
 * Настраивают его редко, а читают в спешке.
 */
/**
 * Замена трекера: сгорел — поставили новый.
 *
 * Отдельным действием, а не полем в общей форме, намеренно. Смена IMEI — это
 * не правка опечатки: для учётной системы машина связана по нему, и молчаливая
 * замена в общей форме оборвала бы её историю пробега посреди года. Здесь у
 * действия своё имя, своя кнопка и своё подтверждение, а постоянный номер
 * машины при этом не меняется — его хранит сервер.
 */
function SwapTrackerBox({
  device,
  t,
  onDone,
}: {
  device: Device
  t: ReturnType<typeof dict>
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const [imei, setImei] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  // Куда вернуть фокус, когда форма закроется: иначе он улетает в body, и
  // обход табом начинается заново — с холста карты.
  const openRef = useRef<HTMLButtonElement>(null)
  const imeiRef = useRef<HTMLInputElement>(null)
  const errorRef = useRef<HTMLDivElement>(null)

  // Форма стоит в конце длинной карточки: сообщение об отказе появлялось выше
  // видимой области, и человек не видел ничего. Подводим его к глазам.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'nearest' })
  }, [error])

  useEffect(() => {
    if (open) imeiRef.current?.focus()
  }, [open])

  async function swap(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.swapTracker(device.id, imei)
      setDone(true)
      setImei('')
      setOpen(false)
      openRef.current?.focus()
      onDone()
    } catch (caught) {
      setError(readable(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h4 className="c-sub">{t.mng.swapTracker}</h4>
      {done && (
        <p className="hint" role="status">
          {t.mng.swapTrackerDone}
        </p>
      )}
      {error && (
        <div className="login-error" role="alert" ref={errorRef}>
          {error}
        </div>
      )}
      {!open ? (
        <button className="act ghost" ref={openRef} onClick={() => setOpen(true)}>
          {t.mng.swapTrackerOpen}
        </button>
      ) : (
        // Настоящая форма, а не набор полей: в поле IMEI жмут Enter, и это
        // должно работать, как в соседних формах этого же экрана.
        <form onSubmit={(e) => void swap(e)}>
          <p className="hint">{t.mng.swapTrackerHint}</p>
          <label className="field">
            <span>{t.mng.imei}</span>
            <input
              value={imei}
              onChange={(e) => setImei(e.target.value)}
              ref={imeiRef}
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
              required
            />
          </label>
          {/* Опасное действие называется опасным и выглядит так же, как
              удаление машины: замена трекера не откатывается кнопкой. */}
          <button className="act danger" disabled={busy || !imei.trim()} type="submit">
            {busy ? t.working : t.mng.swapTrackerDo}
          </button>
          <button
            className="act ghost"
            type="button"
            onClick={() => {
              setOpen(false)
              openRef.current?.focus()
            }}
          >
            {t.confirmNo}
          </button>
        </form>
      )}
    </>
  )
}


function ScheduleBox({ deviceId, t }: { deviceId: number; t: Dict }) {
  const [state, setState] = useState<{ schedule: BlockSchedule | null; running: boolean } | null>(
    null,
  )
  const [draft, setDraft] = useState<BlockSchedule>(DEFAULT_SCHEDULE)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api
      .schedule(deviceId)
      .then((data) => {
        if (cancelled) return
        setState(data)
        if (data.schedule) setDraft(data.schedule)
      })
      .catch((caught) => {
        if (!cancelled) setError(readable(caught, t))
      })
    return () => {
      cancelled = true
    }
  }, [deviceId, t])

  async function save(next: BlockSchedule) {
    setBusy(true)
    setError('')
    try {
      setState(await api.setSchedule(deviceId, next))
      setDraft(next)
    } catch (caught) {
      setError(readable(caught, t))
    } finally {
      setBusy(false)
    }
  }

  async function clear() {
    setBusy(true)
    setError('')
    try {
      await api.clearSchedule(deviceId)
      setState({ schedule: null, running: state?.running ?? false })
      setDraft(DEFAULT_SCHEDULE)
    } catch (caught) {
      setError(readable(caught, t))
    } finally {
      setBusy(false)
    }
  }

  if (state === null) return null

  const on = state.schedule !== null

  return (
    <>
      <h4 className="c-sub">{t.mng.schedule}</h4>

      {/* Настроить расписание и не узнать, что оно не выполняется, — худший
          из исходов: человек уйдёт домой уверенным, что парк заперт. */}
      {on && !state.running && <p className="hint warn">{t.mng.scheduleNotRunning}</p>}

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      {!on ? (
        <button className="act ghost" disabled={busy} onClick={() => void save(DEFAULT_SCHEDULE)}>
          {t.mng.scheduleOff} · {t.mng.save}
        </button>
      ) : (
        <>
          <label className="field">
            <span>{t.mng.workFrom}</span>
            <input
              type="time"
              value={draft.workFrom}
              onChange={(e) => setDraft({ ...draft, workFrom: e.target.value })}
            />
          </label>
          <label className="field">
            <span>{t.mng.workUntil}</span>
            <input
              type="time"
              value={draft.workUntil}
              onChange={(e) => setDraft({ ...draft, workUntil: e.target.value })}
            />
          </label>

          <span className="field-label">{t.mng.workDays}</span>
          <div className="days" role="group" aria-label={t.mng.workDays}>
            {t.mng.weekdays.map((label, index) => {
              const day = index + 1
              const picked = draft.workDays.includes(day)
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={picked}
                  onClick={() =>
                    setDraft({
                      ...draft,
                      workDays: picked
                        ? draft.workDays.filter((d) => d !== day)
                        : [...draft.workDays, day].sort(),
                    })
                  }
                >
                  {label}
                </button>
              )
            })}
          </div>

          <button className="act" disabled={busy} onClick={() => void save(draft)}>
            {busy ? t.working : t.mng.save}
          </button>
          <button className="act ghost" disabled={busy} onClick={() => void clear()}>
            {t.mng.remove}
          </button>
        </>
      )}
    </>
  )
}

/**
 * VIN с шины, только чтение. Номер машины, а не трекера: при переносе трекера
 * на другую машину он сменится сам. Нет VIN — нет и строки.
 */
function VinRow({ position, t }: { position: Position | undefined; t: Dict }) {
  const value = vin(position)
  if (value === null) return null
  return (
    <div className="kv">
      <span>{t.mng.vin}</span>
      <b>{value}</b>
    </div>
  )
}
